/**
 * Google analytics.
 * In all the pages <head> Google analytics js script must be included from Tools Library.
 * example - https://www.philips.com/a1l4rgpa1fx2crzaqh/t1l6vgru59xvprnlz0ll.js
 */
// GaObject object contains list of all object structures which will be used to push into eddlEvents object
const gaObject = {};

/**
 * @description - Define function to push the eddl Action for the cart Page.
 * @param {Array} productsList - Product list of CartData in cart page.
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example gaCartView([]);
 */
gaObject.gaCartView = (productsList) => {
  return {
    trigger: 'ecommerce.checkout.cartview',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: window.eddlContext.locale.currency,
      punchOutSession: document.querySelector('.punchoutStatus').textContent,
      cart: {
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the cart Page share copy link button.
 * @param {Array} productsList - Product list of CartData in cart page.
 * @param {boolean} opportunityIdValue - Describes if an ASM agent has entered an opportunityId in shareCart in the cart page.
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example gaCartShareCopyLink([]);
 */
gaObject.gaCartShareCopyLink = (productsList, opportunityIdValue) => {
  return {
    trigger: 'ecommerce.cart.shareCopyLink',
    event: 'ecommerceEvent',
    ecommerce: {
      shareCart: {
        opportunityID: opportunityIdValue,
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the cart Page save to list button.
 * @param {Array} productsList - Product list of CartData in cart page.
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example gaCartFavView([]);
 */
gaObject.gaCartFavView = (productsList) => {
  return {
    trigger: 'ecommerce.cart.saveToList',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: window.eddlContext.locale.currency,
      punchOutSession: document.querySelector('.punchoutStatus').textContent,
      favourite: {
        actionField: {list: document.querySelector('#cboxContent #ph-newList-cart').value},
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the checkout Page.
 * @param {object} purchaseInfo - Define checkout step
 * @param {Array} productsList - Product list of orderData in order confirmation page.
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example gaOrderConfirmation({}, []);
 */
gaObject.gaOrderConfirmation = (purchaseInfo, productsList) => {
  return {
    trigger: 'ecommerce.checkout.purchase',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: purchaseInfo.currency,
      purchase: {
        actionField: {
          id: purchaseInfo.orderId,
          revenue: purchaseInfo.revenue,
          tax: purchaseInfo.tax,
          shipping: purchaseInfo.shipping,
          shipTo: purchaseInfo.shipTo,
          coupon: purchaseInfo.coupon,
          paymentCardType: purchaseInfo.paymentCardType,
          option: purchaseInfo.option,
          salesOrg: purchaseInfo.salesOrg,
          billTo: purchaseInfo.billTo,
          payTo: purchaseInfo.payTo,
          soldTo: purchaseInfo.soldTo,
          userId: purchaseInfo.userId
        },
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the checkout Page.
 * @param {string} stepValue - Define checkout step
 * @param {Array} productsList - Product list of cartData in checkout Page.
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example checkoutStep('details', []);
 */
gaObject.checkoutStep = (stepValue, productsList) => {
  return {
    trigger: 'ecommerce.checkout.step',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: window.eddlContext.locale.currency,
      checkout: {
        actionField: {
          step: stepValue
        },
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the checkout Page.
 * @param {string} stepValue - Define checkout step
 * @param {string} OptionValue - Payment mode option account or cart.
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example checkoutStepOption('payment', 'account');
 */
gaObject.checkoutStepOption = (stepValue, OptionValue) => {
  return {
    trigger: 'ecommerce.checkout.step.option',
    event: 'ecommerceEvent',
    ecommerce: {
      /* eslint-disable camelcase*/
      checkout_option: {
      /* eslint-enable camelcase*/
        actionField: {
          step: stepValue,
          option: OptionValue
        }
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the productDetailsPage.
 * @param {string} currency - currency code of current session
 * @param {Array} productsList - this will be having values of pageData specific to product informations
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example productDetailsPage([]);
 */
gaObject.productDetailsPage = (currency, productsList) => {
  return {
    trigger: 'ecommerce.product.detail',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: currency,
      punchOutSession: document.querySelector('.punchoutStatus').textContent,
      detail: {
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the productDetailsAddToFav.
 * @param {string} currency - currency code of current session
 * @param {Array} productsList - this will be having values of pageData specific to product informations
 * @returns {object}  - return eddlAction object data to be pushed to eddlEvents
 * @example productDetailsAddToFav([]);
 */
gaObject.productDetailsAddToFav = (currency, productsList) => {
  let listName = '';

  if (document.querySelector('.ph-shoppinglistRdo').checked) {
    listName = document.querySelector('.ph-shoppinglistRdo:checked').value;
  } else {
    listName = document.querySelector('#cboxContent input[name=newListName]').value;
  }

  return {
    trigger: 'ecommerce.product.detail.addToFavourite',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: currency,
      punchOutSession: document.querySelector('.punchoutStatus').textContent,
      favourite: {
        actionField: {list: listName},
        products: productsList
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the addToCartEvent.
 * @param {object} productInfoInput - specific informations of the product which added to cart.
 * Products list Array present in productInfoInput.productsArray.currently for Mylist page
 * @returns {object} return eddlAction object data to be pushed to eddlEvents
 * @example addToCartEvent([]);
 */
gaObject.addToCartEvent = (productInfoInput) => {
  let productsArray = [];
  const productInfo = productInfoInput;
  let punchOutSessionVar = '';
  const productsList = document.querySelectorAll('.ph-group_selectbox_items'),
      productsListArray = [...productsList],
      configOptions = [];

  if (typeof productInfoInput.productsArray === 'undefined') {
    productsListArray.forEach((element) => {
      const itemValue = element.querySelector('select').selectedOptions,
          itemValueArray = [...itemValue];

      if (itemValue.length > 0) {
        itemValueArray.forEach((items) => {
          configOptions.push(items.value);
        });
      }
    });

    const productObject = {
      id: productInfo.productId,
      locale: productInfo.locale.code,
      quantity: productInfo.productQuantity,
      configValue: configOptions
    };

    if (productInfo.productPrice > 0) {
      productObject.price = productInfo.productPrice;
    }

    productsArray.push(productObject);
  } else {
    [...productsArray] = productInfoInput.productsArray;
  }

  if (document.querySelectorAll('.ph-page-productDetails').length > 0 ||
  document.querySelectorAll('.ph-page-search').length > 0 ||
  document.querySelectorAll('.ph-page-productList').length > 0 ||
  document.querySelectorAll('.ph-page-cartPage').length > 0) {
    punchOutSessionVar = document.querySelector('.punchoutStatus').textContent;
  }

  return {
    trigger: 'ecommerce.cart.add',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: productInfo.locale.currency,
      punchOutSession: punchOutSessionVar,
      add: {
        actionField: {list: productInfo.listName},
        products: productsArray
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the addToFavoritePlp.
 * @param {object} productInfoInput - specific informations of the product which added to cart.
 * Products list Array present in productInfoInput.productsArray.currently for Mylist page
 * @returns {object} return eddlAction object data to be pushed to eddlEvents
 * @example addToFavoritePlp([]);
 */
gaObject.addToFavoritePlp = (productInfoInput) => {
  let productsArray = [];
  const productInfo = productInfoInput;
  let punchOutSessionVar = '',
      listName = '';

  if (typeof productInfoInput.productsArray === 'undefined') {
    const productObject = {
      id: productInfo.productId,
      locale: window.eddlContext.locale.currency,
      quantity: productInfo.productQuantity
    };

    if (productInfo.productPrice > 0) {
      productObject.price = productInfo.productPrice;
    }

    productsArray.push(productObject);
  } else {
    [...productsArray] = productInfoInput.productsArray;
  }

  if (document.querySelectorAll('.ph-page-productDetails').length > 0 ||
  document.querySelectorAll('.ph-page-search').length > 0 ||
  document.querySelectorAll('.ph-page-productList').length > 0 ||
  document.querySelectorAll('.ph-page-cartPage').length > 0) {
    punchOutSessionVar = document.querySelector('.punchoutStatus').textContent;
  }

  if (document.querySelector('.ph-shoppinglistRdo').checked) {
    listName = document.querySelector('.ph-shoppinglistRdo:checked').value;
  } else {
    listName = document.querySelector('#cboxContent input[name=newListName]').value;
  }

  return {
    trigger: 'ecommerce.plp.addToFavorite',
    event: 'ecommerceEvent',
    ecommerce: {
      currencyCode: window.eddlContext.locale.currency,
      punchOutSession: punchOutSessionVar,
      favorite: {
        actionField: {list: listName},
        products: productsArray
      }
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the interactionEvent.
 * @param {object} clickEventInfo - Specific informations of the click/interaction event.
 * @returns {object} return eddlAction object data to be pushed to eddlEvents
 * @example interactionEvent([]);
 */
gaObject.interactionEvent = (clickEventInfo) => {
  let micrositeId = '',
      triggerValue = `event.D2BHS.${clickEventInfo.triggerName}`,
      eventValue = 'customEvent';

  if (document.querySelector('meta[name="micrositeId"]')) {
    micrositeId = document.querySelector('meta[name="micrositeId"]').content;
  }

  if (micrositeId === '') {
    micrositeId = document.querySelector('#currentStoreId').value;
  }

  if (clickEventInfo.triggerValue) {
    triggerValue = String(clickEventInfo.triggerValue);
  }

  if (clickEventInfo.eventValue) {
    eventValue = String(clickEventInfo.eventValue);
  }

  return {
    trigger: triggerValue,
    event: eventValue,
    interaction: clickEventInfo.interaction,
    component: {
      name: 'D2BHS',
      type: clickEventInfo.componentType,
      id: micrositeId
    }
  };
};

/**
 * @description - Define function to push the eddl Action for the orderHistory productClick interactionEvent.
 * @param {object} clickEventInfo - Specific informations of the click/interaction event.
 * @returns {object} return eddlAction object data to be pushed to eddlEvents
 * @example orderHistprodClickEvent([]);
 */
gaObject.orderHistprodClickEvent = (clickEventInfo) => {
  return {
    trigger: `event.D2BHS.${clickEventInfo.triggerName}`,
    event: 'userEvent',
    interaction: clickEventInfo.interaction,
    product: clickEventInfo.productId,
    component: {
      name: 'order-history',
      type: 'page',
      instance: clickEventInfo.instance,
      element: clickEventInfo.orderId
    }
  };
};
